import { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '../components/ui/card';
import {
  Target, Play, Square, RefreshCw, Eye, TrendingUp, TrendingDown,
  ArrowUpDown, Layers, AlertCircle, Wallet, ClipboardPaste, CheckCircle,
  XCircle, Flame, Droplets, BarChart2, Clock, Zap, Activity,
  ChevronDown, ChevronUp, ExternalLink, Shield, Info,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import TokenSafetyCheck from '../components/TokenSafetyCheck';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CHAIN_MAP = {
  pump_fun:    'solana',
  raydium:     'solana',
  jupiter:     'solana',
  uniswap:     'evm',
  pancakeswap: 'bsc',
};

function isValidAddress(address, chain) {
  if (!address || address.length < 20) return false;
  if (chain === 'solana') return /^[1-9A-HJ-NP-Za-km-z]{32,50}$/.test(address);
  return /^0x[0-9a-fA-F]{40,}$/.test(address);
}

const fmt   = (n, d = 2) => (parseFloat(n) || 0).toFixed(d);
const pnlCls = (n) => parseFloat(n) > 0 ? 'text-green-400' : parseFloat(n) < 0 ? 'text-red-400' : 'text-slate-400';

// ── small shared UI pieces ─────────────────────────────────────────────────
function StatPill({ label, value, accent }) {
  return (
    <div className="flex flex-col items-center px-3 py-2 rounded-lg bg-slate-700/50 min-w-[72px]">
      <span className={`text-sm font-bold font-mono ${accent || 'text-white'}`}>{value}</span>
      <span className="text-[10px] text-slate-400 mt-0.5 text-center leading-tight">{label}</span>
    </div>
  );
}

function SellBlockedBadge({ reason }) {
  if (!reason) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-yellow-900/40 text-yellow-300 border border-yellow-700/30">
      <Shield className="w-2.5 h-2.5" /> {reason}
    </span>
  );
}

function BotStatusBadge({ status }) {
  const map = {
    running: 'bg-green-600/20 text-green-300 border-green-600/30',
    stopped: 'bg-slate-600/20 text-slate-400 border-slate-600/30',
    error:   'bg-red-600/20 text-red-300 border-red-600/30',
  };
  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border capitalize ${map[status] || map.stopped}`}>
      {status === 'running' && <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-400 mr-1 animate-pulse" />}
      {status || 'stopped'}
    </span>
  );
}

// ── main component ─────────────────────────────────────────────────────────
export default function LureBot() {
  const { panicMode } = useOutletContext();
  const { isSubUser } = useAuth();

  const [bots, setBots]                     = useState([]);
  const [loading, setLoading]               = useState(false);
  const [exchanges, setExchanges]           = useState([]);
  const [selectedExchange, setSelectedExchange] = useState('');
  const [tokens, setTokens]                 = useState([]);
  const [selectedToken, setSelectedToken]   = useState(null);
  const [pasteAddress, setPasteAddress]     = useState('');
  const [walletStatus, setWalletStatus]     = useState(null);
  const [expandedBot, setExpandedBot]       = useState(null);
  const [safetyBlocked, setSafetyBlocked]   = useState(false);

  // suggestions
  const [suggestions, setSuggestions]               = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestionChainFilter, setSuggestionChainFilter] = useState('all');
  const [suggestionsExpanded, setSuggestionsExpanded] = useState(true);

  const [config, setConfig] = useState({
    micro_buy_amount:        5,
    frequency:               20,
    enabled:                 true,
    paper_investment_sol:    0.05,
    take_profit_multiplier:  2.0,
    min_profit_after_fees_pct: 5.0,
    paper_mode:              false,
  });

  const intervalRef = useRef(null);

  // ── fetch bots ────────────────────────────────────────────────────────
  const fetchBots = useCallback(async () => {
    try {
      if (isSubUser) {
        const { data } = await axios.get(`${API_URL}/api/subuser/bots`);
        const normalized = (data.bots || [])
          .filter(b => b.bot_type === 'LURE')
          .map(b => ({
            ...b,
            id:                   b.bot_id,
            status:               b.status?.toLowerCase(),
            micro_buys_executed:  b.total_trades || 0,
            micro_sells_executed: 0,
            total_buy_volume:     b.total_volume_usd || 0,
            realized_pnl:         b.total_earnings_usd || 0,
            trackers_triggered:   [],
          }));
        setBots(normalized);
      } else {
        const { data } = await axios.get(`${API_URL}/api/bot/lure/status`);
        setBots(data.bots || []);
      }
    } catch (err) {
      console.error('Failed to fetch lure bots:', err);
    }
  }, [isSubUser]);

  // ── fetch exchanges ───────────────────────────────────────────────────
  const fetchExchanges = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/defi/exchanges`);
      setExchanges(data.exchanges || []);
    } catch (err) { console.error(err); }
  }, []);

  // ── fetch wallet status ───────────────────────────────────────────────
  const fetchWalletStatus = useCallback(async () => {
    try {
      const [walletsRes, connectionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/wallet/connected`),
        axios.get(`${API_URL}/api/exchange/connections`),
      ]);
      setWalletStatus({
        wallets:     walletsRes.data.wallets || [],
        connections: connectionsRes.data.connections || [],
      });
    } catch (err) { console.error(err); }
  }, []);

  // ── fetch suggestions ─────────────────────────────────────────────────
  const fetchSuggestions = useCallback(async (forceRefresh = false) => {
    setLoadingSuggestions(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/admin/suggested-tokens${forceRefresh ? '?refresh=true' : ''}`
      );
      setSuggestions(data.tokens || data.suggestions || []);
    } catch { /* silently fail — suggestions are optional */ }
    finally { setLoadingSuggestions(false); }
  }, []);

  // ── init + polling ────────────────────────────────────────────────────
  useEffect(() => {
    fetchBots();
    fetchExchanges();
    fetchWalletStatus();
    fetchSuggestions();
    intervalRef.current = setInterval(fetchBots, 3000);
    return () => clearInterval(intervalRef.current);
  }, [fetchBots, fetchExchanges, fetchWalletStatus, fetchSuggestions]);

  // ── suggestion click → auto-fill form ────────────────────────────────
  const handleSuggestionClick = async (token) => {
    const targetExchange = token.exchange_id || 'jupiter';
    setSelectedExchange(targetExchange);
    setSelectedToken(null);
    setPasteAddress(token.address || '');
    try {
      const { data } = await axios.get(`${API_URL}/api/defi/exchanges/${targetExchange}/tokens`);
      setTokens(data.tokens || []);
      const known = (data.tokens || []).find(t => t.address === token.address);
      setSelectedToken(known || {
        symbol:    token.symbol,
        name:      token.name || token.symbol,
        address:   token.address,
        price:     token.price_usd || 0,
        change_24h: token.price_change_24h || 0,
      });
    } catch {
      setSelectedToken({
        symbol:    token.symbol,
        name:      token.name || token.symbol,
        address:   token.address,
        price:     token.price_usd || 0,
        change_24h: token.price_change_24h || 0,
      });
    }
    const walletNeeded = token.chain === 'solana' ? 'Phantom' : 'MetaMask';
    toast.success(`✅ ${token.symbol} selected · ${token.chain?.toUpperCase()} · needs ${walletNeeded}`);
  };

  // ── exchange change ───────────────────────────────────────────────────
  const handleExchangeChange = async (exchangeId) => {
    setSelectedExchange(exchangeId);
    setSelectedToken(null);
    setPasteAddress('');
    if (!exchangeId) { setTokens([]); return; }
    try {
      const { data } = await axios.get(`${API_URL}/api/defi/exchanges/${exchangeId}/tokens`);
      setTokens(data.tokens || []);
    } catch { toast.error('Failed to fetch tokens'); }
  };

  // ── paste/validate custom address ────────────────────────────────────
  const handlePasteAddress = () => {
    if (!selectedExchange) { toast.error('Select an exchange first'); return; }
    const chain = CHAIN_MAP[selectedExchange] || 'solana';
    if (!isValidAddress(pasteAddress, chain)) {
      toast.error(`Invalid ${chain === 'solana' ? 'Solana' : 'EVM'} contract address`);
      return;
    }
    const known = tokens.find(t => t.address === pasteAddress);
    if (known) {
      setSelectedToken(known);
      toast.success(`Matched known token: ${known.symbol}`);
    } else {
      setSelectedToken({ symbol: `CUSTOM-${pasteAddress.slice(0, 6)}`, address: pasteAddress, price: 0, change_24h: 0 });
      toast.success('Custom token address set');
    }
  };

  // ── wallet resolver ───────────────────────────────────────────────────
  const getWalletForExchange = () => {
    if (!selectedExchange || !walletStatus) return null;
    const chain        = CHAIN_MAP[selectedExchange];
    const expectedType = chain === 'solana' ? 'phantom' : 'metamask';
    const conn = walletStatus.connections.find(c => c.exchange_id === selectedExchange);
    if (conn) return { address: conn.wallet_address, type: conn.wallet_type || expectedType, connected: true };
    const w = walletStatus.wallets.find(w => w.chain === chain || w.wallet_type === expectedType);
    if (w) return { address: w.address, type: w.wallet_type || expectedType, connected: true };
    return null;
  };
  const walletForExchange = getWalletForExchange();

  // ── start bot ─────────────────────────────────────────────────────────
  const handleStartBot = async () => {
    if (panicMode)                        { toast.error('Cannot start bots in panic mode'); return; }
    if (!selectedToken || !selectedExchange) { toast.error('Select a DeFi exchange and token pair first'); return; }
    if (!walletForExchange)               { toast.error('Connect your wallet on the Exchange Connect page first'); return; }

    setLoading(true);
    try {
      if (isSubUser) {
        const { data } = await axios.post(`${API_URL}/api/subuser/bots`, {
          bot_type:      'LURE',
          exchange_id:   selectedExchange,
          token_symbol:  selectedToken.symbol,
          token_address: selectedToken.address,
          config: {
            micro_buy_amount:          config.micro_buy_amount,
            frequency:                 config.frequency,
            paper_investment_sol:      config.paper_investment_sol,
            take_profit_multiplier:    config.take_profit_multiplier,
            min_profit_after_fees_pct: config.min_profit_after_fees_pct,
          },
        });
        toast.success(data.message);
      } else {
        const { data } = await axios.post(`${API_URL}/api/bot/lure/start`, {
          token_address:             selectedToken.address,
          token_symbol:              selectedToken.symbol,
          exchange_id:               selectedExchange,
          exchange_name:             exchanges.find(e => e.id === selectedExchange)?.name || '',
          micro_buy_amount:          config.micro_buy_amount,
          frequency:                 config.frequency,
          enabled:                   config.enabled,
          paper_investment_sol:      config.paper_investment_sol,
          take_profit_multiplier:    config.take_profit_multiplier,
          min_profit_after_fees_pct: config.min_profit_after_fees_pct,
          paper_mode:                config.paper_mode,
        });
        toast.success(`LureBot started — ${data.bot_id}`);
      }
      fetchBots();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start bot');
    } finally {
      setLoading(false);
    }
  };

  // ── stop / restart ────────────────────────────────────────────────────
  const handleStopBot = async (botId) => {
    try {
      if (isSubUser) await axios.post(`${API_URL}/api/subuser/bots/${botId}/stop`);
      else           await axios.post(`${API_URL}/api/bot/lure/stop/${botId}`);
      toast.success('Bot stopped');
      fetchBots();
    } catch { toast.error('Failed to stop bot'); }
  };

  const handleRestartBot = async (botId) => {
    try {
      await axios.post(`${API_URL}/api/bot/lure/restart/${botId}`);
      toast.success('Bot restarted with saved config!');
      fetchBots();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to restart bot'); }
  };

  // ── aggregated stats ──────────────────────────────────────────────────
  const activeBots       = bots.filter(b => b.status === 'running').length;
  const totalMicroBuys   = bots.reduce((s, b) => s + (b.micro_buys_executed  || 0), 0);
  const totalMicroSells  = bots.reduce((s, b) => s + (b.micro_sells_executed || 0), 0);
  const totalRealizedPnL = bots.reduce((s, b) => s + (b.realized_pnl        || 0), 0);
  const totalUnrealizedPnL = bots.reduce((s, b) => s + (b.unrealized_pnl    || 0), 0);
  const allTrackers      = [...new Set(bots.flatMap(b => b.trackers_triggered || []))];

  // suggestions filter
  const availableChains      = ['all', ...new Set(suggestions.map(t => t.chain).filter(Boolean))];
  const filteredSuggestions  = suggestionChainFilter === 'all'
    ? suggestions
    : suggestions.filter(t => t.chain === suggestionChainFilter);

  // ── render ─────────────────────────────────────────────────────────────
  return (
    <div className="p-4 md:p-6 space-y-6 max-w-6xl mx-auto">

      {/* ── page header ── */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Target className="w-5 h-5 text-purple-400" />
            LureBot
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">
            Micro-buy accumulate → sell to SOL only when profitable
          </p>
        </div>
        {panicMode && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-900/30 border border-red-700/40 text-red-300 text-xs font-semibold">
            <AlertCircle className="w-3.5 h-3.5" /> PANIC MODE — all bots halted
          </div>
        )}
      </div>

      {/* ── summary stats bar ── */}
      {bots.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <StatPill label="Active"         value={activeBots}                          accent={activeBots > 0 ? 'text-green-400' : 'text-slate-400'} />
          <StatPill label="Total Bots"     value={bots.length} />
          <StatPill label="Micro Buys"     value={totalMicroBuys} />
          <StatPill label="Micro Sells"    value={totalMicroSells} />
          <StatPill label="Realized PnL"   value={`${totalRealizedPnL >= 0 ? '+' : ''}$${fmt(totalRealizedPnL)}`} accent={pnlCls(totalRealizedPnL)} />
          <StatPill label="Unrealized"     value={`${totalUnrealizedPnL >= 0 ? '+' : ''}$${fmt(totalUnrealizedPnL)}`} accent={pnlCls(totalUnrealizedPnL)} />
          {allTrackers.length > 0 && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-orange-900/30 border border-orange-700/30">
              <Flame className="w-3 h-3 text-orange-400" />
              <span className="text-[10px] text-orange-300">{allTrackers.join(' · ')}</span>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* ══════════════════════════════════════════════
            LEFT COLUMN — config + suggestions
        ══════════════════════════════════════════════ */}
        <div className="lg:col-span-1 space-y-4">

          {/* ── suggested tokens ── */}
          <Card className="bg-slate-800/60 border-slate-700">
            <CardHeader className="pb-2 pt-4 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
                  <Flame className="w-4 h-4 text-orange-400" /> Suggested Tokens
                </CardTitle>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => fetchSuggestions(true)}
                    className="text-slate-500 hover:text-slate-300"
                    title="Refresh suggestions"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${loadingSuggestions ? 'animate-spin' : ''}`} />
                  </button>
                  <button
                    onClick={() => setSuggestionsExpanded(p => !p)}
                    className="text-slate-500 hover:text-slate-300"
                  >
                    {suggestionsExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>

              {suggestionsExpanded && availableChains.length > 1 && (
                <div className="flex gap-1 mt-2 flex-wrap">
                  {availableChains.map(chain => (
                    <button
                      key={chain}
                      onClick={() => setSuggestionChainFilter(chain)}
                      className={`text-[10px] px-2 py-0.5 rounded-full border capitalize transition-colors ${
                        suggestionChainFilter === chain
                          ? 'bg-purple-700 border-purple-500 text-white'
                          : 'border-slate-600 text-slate-400 hover:border-slate-500'
                      }`}
                    >
                      {chain}
                    </button>
                  ))}
                </div>
              )}
            </CardHeader>

            {suggestionsExpanded && (
              <CardContent className="px-4 pb-4">
                {loadingSuggestions ? (
                  <div className="space-y-2">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="h-12 bg-slate-700 rounded animate-pulse" />
                    ))}
                  </div>
                ) : filteredSuggestions.length === 0 ? (
                  <p className="text-xs text-slate-500 text-center py-3">No suggestions available</p>
                ) : (
                  <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                    {filteredSuggestions.map((token, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSuggestionClick(token)}
                        className="w-full flex items-center justify-between p-2 rounded-lg bg-slate-700/50 hover:bg-slate-700 border border-slate-600/40 hover:border-purple-600/40 transition-colors text-left"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-bold text-white">{token.symbol}</span>
                            <span className="text-[9px] px-1 py-0.5 rounded bg-slate-600 text-slate-400 capitalize">{token.chain}</span>
                          </div>
                          <p className="text-[10px] text-slate-400 truncate mt-0.5">{token.name || token.address?.slice(0, 14) + '...'}</p>
                        </div>
                        <div className="text-right shrink-0 ml-2">
                          {token.price_usd > 0 && (
                            <p className="text-[10px] font-mono text-slate-300">${parseFloat(token.price_usd).toFixed(6)}</p>
                          )}
                          {token.price_change_24h !== undefined && (
                            <p className={`text-[10px] font-mono ${parseFloat(token.price_change_24h) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {parseFloat(token.price_change_24h) >= 0 ? '+' : ''}{fmt(token.price_change_24h)}%
                            </p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {/* ── token selection ── */}
          <Card className="bg-slate-800/60 border-slate-700">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
                <Droplets className="w-4 h-4 text-blue-400" /> Token Selection
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">

              {/* exchange dropdown */}
              <div className="space-y-1">
                <Label className="text-xs text-slate-400">Exchange</Label>
                <select
                  value={selectedExchange}
                  onChange={e => handleExchangeChange(e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-md px-3 h-9 focus:outline-none focus:border-purple-500"
                >
                  <option value="">Select exchange…</option>
                  {exchanges.map(ex => (
                    <option key={ex.id} value={ex.id}>{ex.name}</option>
                  ))}
                </select>
              </div>

              {/* known token list */}
              {tokens.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400">Token</Label>
                  <select
                    value={selectedToken?.address || ''}
                    onChange={e => setSelectedToken(tokens.find(t => t.address === e.target.value) || null)}
                    className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded-md px-3 h-9 focus:outline-none focus:border-purple-500"
                  >
                    <option value="">Select token…</option>
                    {tokens.map(t => (
                      <option key={t.address} value={t.address}>{t.symbol} — {t.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* custom paste */}
              <div className="space-y-1">
                <Label className="text-xs text-slate-400">Or paste contract address</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Token address…"
                    value={pasteAddress}
                    onChange={e => setPasteAddress(e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white h-9 text-sm font-mono flex-1"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handlePasteAddress}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700 h-9 px-2 shrink-0"
                  >
                    <ClipboardPaste className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* selected token preview */}
              {selectedToken && (
                <div className="flex items-center justify-between p-2 rounded-lg bg-purple-900/20 border border-purple-700/30">
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-purple-300">{selectedToken.symbol}</p>
                    <p className="text-[10px] text-slate-400 font-mono truncate">{selectedToken.address}</p>
                  </div>
                  <div className="text-right shrink-0 ml-2">
                    {parseFloat(selectedToken.price) > 0 && (
                      <p className="text-[10px] font-mono text-slate-300">${parseFloat(selectedToken.price).toFixed(6)}</p>
                    )}
                    {selectedToken.change_24h !== undefined && (
                      <p className={`text-[10px] ${parseFloat(selectedToken.change_24h) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {parseFloat(selectedToken.change_24h) >= 0 ? '+' : ''}{fmt(selectedToken.change_24h)}%
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Trust Layer — pre-trade safety check */}
              {selectedToken?.address && (
                <TokenSafetyCheck
                  mint={selectedToken.address}
                  source="commander-lure-bot"
                  onGate={setSafetyBlocked}
                />
              )}

              {/* wallet status */}
              {selectedExchange && (
                <div className={`flex items-center gap-2 p-2 rounded-lg border text-xs ${
                  walletForExchange
                    ? 'bg-green-900/20 border-green-700/30 text-green-300'
                    : 'bg-yellow-900/20 border-yellow-700/30 text-yellow-300'
                }`}>
                  <Wallet className="w-3.5 h-3.5 shrink-0" />
                  {walletForExchange
                    ? `${walletForExchange.address.slice(0, 8)}…${walletForExchange.address.slice(-6)}`
                    : 'No wallet connected — go to Exchange Connect'}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── bot config ── */}
          <Card className="bg-slate-800/60 border-slate-700">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
                <Layers className="w-4 h-4 text-slate-400" /> Bot Config
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">

              {/* ── Micro-Buy Settings ── */}
              <div className="p-3 rounded-lg bg-emerald-900/20 border border-emerald-700/30 space-y-3">
                <p className="text-xs font-semibold text-emerald-400 flex items-center gap-1.5">
                  <TrendingUp className="w-3.5 h-3.5" /> Micro-Buy Settings
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-400">Micro Buy (USD)</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={config.micro_buy_amount}
                      onChange={e => { const v = e.target.value; if (/^(\d+\.?\d*|\.\d*)$/.test(v) || v === '') setConfig(p => ({ ...p, micro_buy_amount: v })); }}
                      className="bg-slate-700 border-slate-600 text-white h-9 text-sm [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-400">Frequency (s)</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={config.frequency}
                      onChange={e => { const v = e.target.value; if (/^\d*$/.test(v) || v === '') setConfig(p => ({ ...p, frequency: v })); }}
                      className="bg-slate-700 border-slate-600 text-white h-9 text-sm [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                  </div>
                </div>
              </div>

              {/* ── Profit-Only Sell Settings ── */}
              <div className="p-3 rounded-lg bg-green-900/20 border border-green-700/30 space-y-3">
                <p className="text-xs font-semibold text-green-400 flex items-center gap-1.5">
                  <TrendingDown className="w-3.5 h-3.5" /> Sell Settings — SOL only
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-400">Take Profit (×)</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={config.take_profit_multiplier}
                      onChange={e => { const v = e.target.value; if (/^(\d+\.?\d*|\.\d*)$/.test(v) || v === '') setConfig(p => ({ ...p, take_profit_multiplier: v })); }}
                      className="bg-slate-700 border-slate-600 text-white h-9 text-sm [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                    <p className="text-[10px] text-slate-500">2.0 = sell at 2× buy cost</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-400">Min Profit %</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={config.min_profit_after_fees_pct}
                      onChange={e => { const v = e.target.value; if (/^(\d+\.?\d*|\.\d*)$/.test(v) || v === '') setConfig(p => ({ ...p, min_profit_after_fees_pct: v })); }}
                      className="bg-slate-700 border-slate-600 text-white h-9 text-sm [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                    <p className="text-[10px] text-slate-500">Never sells at a loss</p>
                  </div>
                </div>
              </div>

              {/* ── Paper Trading ── */}
              <div className="p-3 rounded-lg bg-yellow-900/20 border border-yellow-700/30 space-y-2">
                <p className="text-xs font-semibold text-yellow-400 flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5" /> Paper Trading (sim only)
                </p>
                <div className="grid grid-cols-2 gap-3 items-center">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-400">Paper SOL / cycle</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={config.paper_investment_sol}
                      onChange={e => { const v = e.target.value; if (/^(\d+\.?\d*|\.\d*)$/.test(v) || v === '') setConfig(p => ({ ...p, paper_investment_sol: v })); }}
                      className="bg-slate-700 border-yellow-700/40 text-white h-9 text-sm [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                  </div>
                  <div className="text-[10px] text-yellow-400/70 font-mono space-y-0.5 pt-5">
                    <div>≈ ${(parseFloat(config.paper_investment_sol) * 150 || 0).toFixed(2)} USD</div>
                    <div>No on-chain tx</div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between py-1">
                <Label className="text-xs text-slate-400">Enabled</Label>
                <Switch
                  checked={config.enabled}
                  onCheckedChange={v => setConfig(p => ({ ...p, enabled: v }))}
                />
              </div>

              <div className={`flex items-center justify-between py-1 px-2 rounded-lg border ${
                config.paper_mode
                  ? 'bg-yellow-900/20 border-yellow-700/30'
                  : 'bg-green-900/20 border-green-700/30'
              }`}>
                <div className="flex flex-col">
                  <Label className={`text-xs font-semibold ${config.paper_mode ? 'text-yellow-300' : 'text-green-300'}`}>
                    {config.paper_mode ? '📄 Paper Mode' : '🟢 Live Mode'}
                  </Label>
                  <span className="text-[10px] text-slate-500 mt-0.5">
                    {config.paper_mode ? 'Simulates trades — no SOL spent' : 'Executes real swaps on-chain'}
                  </span>
                </div>
                <Switch
                  checked={config.paper_mode}
                  onCheckedChange={v => setConfig(p => ({ ...p, paper_mode: v }))}
                />
              </div>
              <div className="p-2 rounded-lg bg-blue-900/20 border border-blue-700/30 flex items-start gap-2">
                <Info className="w-3.5 h-3.5 text-blue-400 mt-0.5 shrink-0" />
                <p className="text-[10px] text-blue-300 leading-relaxed">
                  Sell output is always locked to <strong>SOL</strong>. Profit gate blocks sells unless projected return exceeds total SOL spent.
                </p>
              </div>

              <Button
                className="w-full bg-purple-700 hover:bg-purple-600 text-white h-9 font-semibold text-sm"
                onClick={handleStartBot}
                disabled={loading || panicMode || !selectedToken || !walletForExchange || safetyBlocked}
              >
                {loading
                  ? <><RefreshCw className="w-3.5 h-3.5 mr-2 animate-spin" /> Starting…</>
                  : <><Play className="w-3.5 h-3.5 mr-2" /> Start LureBot</>
                }
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* ══════════════════════════════════════════════
            RIGHT COLUMN — running bots
        ══════════════════════════════════════════════ */}
        <div className="lg:col-span-2 space-y-3">

          {/* empty state */}
          {bots.length === 0 && (
            <Card className="bg-slate-800/60 border-slate-700">
              <CardContent className="py-14 flex flex-col items-center text-center gap-3">
                <Target className="w-12 h-12 text-slate-600" />
                <p className="text-slate-400 font-medium">No LureBots yet</p>
                <p className="text-sm text-slate-500 max-w-xs">
                  Select an exchange, pick a token, configure your micro-buy amount and hit <strong>Start LureBot</strong>.
                </p>
              </CardContent>
            </Card>
          )}

          {/* bot cards */}
          {bots.map((bot) => {
            const isRunning  = bot.status === 'running';
            const isExpanded = expandedBot === (bot.id || bot.bot_id);
            const botId      = bot.id || bot.bot_id;
            const realizedPnl   = parseFloat(bot.realized_pnl || 0);
            const paperPnl      = parseFloat(bot.paper_pnl || 0);
            const unrealizedPnl = parseFloat(bot.unrealized_pnl || 0);
            const tokensHeld    = parseInt(bot.tokens_held || 0);
            const totalBuySol   = parseFloat(bot.total_buy_sol || 0);
            const execMode      = bot.execution_mode || 'paper';
            const isPaperBot    = execMode === 'paper' || bot.config?.paper_mode;
            const displayPnl    = isPaperBot ? paperPnl : realizedPnl;

            return (
              <Card
                key={botId}
                className={`border transition-colors ${
                  isRunning ? 'bg-slate-800/70 border-purple-700/30' : 'bg-slate-800/40 border-slate-700'
                }`}
              >
                {/* ── card header row ── */}
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">

                    {/* left: token info + status */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-white">{bot.token_symbol || 'Unknown'}</span>
                        <BotStatusBadge status={bot.status} />
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                          execMode === 'mainnet' || execMode === 'live'
                            ? 'bg-green-900/30 border-green-700/30 text-green-300'
                            : 'bg-slate-700/50 border-slate-600 text-slate-400'
                        }`}>
                          {isPaperBot ? '📄 Paper' : '🟢 Live'}
                        </span>
                      </div>
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        {bot.exchange_name || bot.exchange_id} · {botId}
                      </p>
                      {bot.last_sell_blocked && (
                        <div className="mt-1">
                          <SellBlockedBadge reason={bot.last_sell_blocked} />
                        </div>
                      )}
                      {bot.last_error && (
                        <p className="text-[10px] text-red-400 mt-1 truncate">⚠ {bot.last_error}</p>
                      )}
                    </div>

                    {/* right: key metrics */}
                    <div className="flex items-center gap-1.5 flex-wrap justify-end">
                      <div className="text-right">
                        <p className={`text-sm font-bold font-mono ${pnlCls(displayPnl)}`}>
                          {displayPnl >= 0 ? '+' : ''}${fmt(displayPnl)}
                        </p>
                        <p className="text-[10px] text-slate-500">{isPaperBot ? 'paper pnl' : 'realized'}</p>
                      </div>
                      {tokensHeld > 0 && (
                        <div className="text-right">
                          <p className={`text-sm font-bold font-mono ${pnlCls(unrealizedPnl)}`}>
                            {unrealizedPnl >= 0 ? '+' : ''}${fmt(unrealizedPnl)}
                          </p>
                          <p className="text-[10px] text-slate-500">unrealized</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ── metrics pills row ── */}
                  <div className="flex flex-wrap gap-2 mt-3">
                    <div className="flex items-center gap-1 text-[10px] text-slate-400">
                      <ArrowUpDown className="w-3 h-3" />
                      {bot.micro_buys_executed || 0} buys / {bot.micro_sells_executed || 0} sells
                    </div>
                    {tokensHeld > 0 && (
                      <div className="flex items-center gap-1 text-[10px] text-slate-400">
                        <Layers className="w-3 h-3" />
                        {tokensHeld.toLocaleString()} tokens held
                      </div>
                    )}
                    {totalBuySol > 0 && (
                      <div className="flex items-center gap-1 text-[10px] text-slate-400">
                        <Zap className="w-3 h-3" />
                        Cost basis: {fmt(totalBuySol, 4)} SOL
                      </div>
                    )}
                    {bot.total_buy_volume > 0 && (
                      <div className="flex items-center gap-1 text-[10px] text-slate-400">
                        <BarChart2 className="w-3 h-3" />
                        Vol: ${fmt(bot.total_buy_volume)}
                      </div>
                    )}
                    {bot.sell_every_n_buys && (
                      <div className="flex items-center gap-1 text-[10px] text-slate-400">
                        <Clock className="w-3 h-3" />
                        Sell every {bot.sell_every_n_buys} buys
                      </div>
                    )}
                  </div>

                  {/* ── tracker badges ── */}
                  {(bot.trackers_triggered || []).length > 0 && (
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {bot.trackers_triggered.map((t, i) => (
                        <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-orange-900/30 border border-orange-700/30 text-orange-300">
                          {t}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* ── action buttons ── */}
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    {isRunning ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleStopBot(botId)}
                        className="border-red-700/50 text-red-300 hover:bg-red-900/30 h-7 px-3 text-xs"
                      >
                        <Square className="w-3 h-3 mr-1" /> Stop
                      </Button>
                    ) : (
                      !isSubUser && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRestartBot(botId)}
                          className="border-purple-700/50 text-purple-300 hover:bg-purple-900/30 h-7 px-3 text-xs"
                        >
                          <RefreshCw className="w-3 h-3 mr-1" /> Restart
                        </Button>
                      )
                    )}
                    <button
                      onClick={() => setExpandedBot(isExpanded ? null : botId)}
                      className="flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-300 ml-auto"
                    >
                      <Eye className="w-3 h-3" />
                      {isExpanded ? 'Hide' : 'Logs'}
                      {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>
                  </div>

                  {/* ── expanded swap log ── */}
                  {isExpanded && (
                    <div className="mt-3 pt-3 border-t border-slate-700">
                      <p className="text-[10px] text-slate-500 mb-2">Recent swap log (last 10)</p>
                      {(bot.swap_logs || []).length === 0 ? (
                        <p className="text-[10px] text-slate-600 italic">No swaps recorded yet</p>
                      ) : (
                        <div className="space-y-1 max-h-44 overflow-y-auto pr-1">
                          {[...(bot.swap_logs || [])].reverse().slice(0, 10).map((log, idx) => (
                            <div key={idx} className="flex items-center justify-between text-[10px] py-1 border-b border-slate-700/40 last:border-0">
                              <div className="flex items-center gap-1.5 min-w-0">
                                {log.success
                                  ? <CheckCircle className="w-3 h-3 text-green-400 shrink-0" />
                                  : <XCircle className="w-3 h-3 text-red-400 shrink-0" />
                                }
                                <span className="capitalize text-slate-400">{(log.type || 'swap').replace(/_/g, ' ')}</span>
                                {log.buy_usd  && <span className="text-slate-500">buy ${fmt(log.buy_usd)}</span>}
                                {log.sell_usd && <span className="text-slate-500">sell ${fmt(log.sell_usd)}</span>}
                                {log.pnl_usd !== undefined && (
                                  <span className={pnlCls(log.pnl_usd)}>
                                    {parseFloat(log.pnl_usd) >= 0 ? '+' : ''}${fmt(log.pnl_usd, 4)}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1 shrink-0 ml-2">
                                {log.output && (
                                  <span className="text-[9px] px-1 rounded bg-slate-700 text-slate-400">{log.output}</span>
                                )}
                                {(log.solscan_buy || log.solscan_sell || log.solscan_url || log.tx_signature) && (
                                  <div className="flex items-center gap-1">
                                    {log.solscan_buy && (
                                      <a
                                        href={log.solscan_buy}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[9px] text-blue-400 hover:text-blue-300 hover:underline flex items-center gap-0.5"
                                        title="View buy transaction on Solscan"
                                      >
                                        <ExternalLink className="w-2.5 h-2.5" /> Buy
                                      </a>
                                    )}
                                    {log.solscan_sell && (
                                      <a
                                        href={log.solscan_sell}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[9px] text-green-400 hover:text-green-300 hover:underline flex items-center gap-0.5"
                                        title="View sell transaction on Solscan"
                                      >
                                        <ExternalLink className="w-2.5 h-2.5" /> Sell
                                      </a>
                                    )}
                                    {/* micro_buy logs use solscan_url field */}
                                    {log.solscan_url && !log.solscan_buy && !log.solscan_sell && (
                                      <a
                                        href={log.solscan_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[9px] text-blue-400 hover:text-blue-300 hover:underline flex items-center gap-0.5"
                                        title="View transaction on Solscan"
                                      >
                                        <ExternalLink className="w-2.5 h-2.5" /> Solscan
                                      </a>
                                    )}
                                    {!log.solscan_buy && !log.solscan_sell && !log.solscan_url && log.tx_signature && (
                                      <a
                                        href={`https://solscan.io/tx/${log.tx_signature}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[9px] text-blue-400 hover:text-blue-300 hover:underline flex items-center gap-0.5"
                                        title="View on Solscan"
                                      >
                                        <ExternalLink className="w-2.5 h-2.5" /> Solscan
                                      </a>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
